import { DetachFromDomIterator } from '../Iterator.js';
import { UpdateMaterialImageVisitor } from '../UpdateMaterialImageVisitor.js';
import {
  ChildVisitOrder, ProductDrawingVisitor, ToggleLabelVisitor, SizeStrategy, DefaultSizeOptions,
  DoorSizeOptions, OrganizerSizeOptions
} from '../ProductVisitors.js';
import { ObjectUtils } from '../../main/ObjectUtils.js';

export class HtmlRenderVisitor extends ProductDrawingVisitor {
  constructor(finishResources, drawingElement) {
    super();
    this.finishResources = finishResources;
    // DOM elements:
    this.drawingElement = drawingElement;
    // The visitor wants to control when the children are visited:
    this.childVisitOrder = ChildVisitOrder.noChildren;
    /** When isInlineUpdate is true, the component structure has not changed and there's no need to
     * recreate any of the html elements. Instead each element is updated inline. */
    this.isInlineUpdate = false;
  }

  //#region *** Overridden methods ***

  doBeforeVisit(guest) {
    super.doBeforeVisit(guest);
    this.rootElement = document.createDocumentFragment();
    this.pushContext(this.rootElement);
  }

  doAfterVisit(guest) {
    super.doAfterVisit(guest);
    this.drawingElement.appendChild(this.rootElement);
    this.popContext();
  }

  clearDrawing() {
    super.clearDrawing();
    /* The iterator will call the detachFromUI() Component method to remove the bilateral reference(s)
     * between component and dom element. */
    const detachIterator = new DetachFromDomIterator();
    const parent = this.drawingElement;
    if (parent) {
      // Assuming removing lastChild is faster than firstChild...
      while (parent.lastChild) {
        if (parent.lastChild.dataComponent) {
          detachIterator.iterate(parent.lastChild.dataComponent);
        }
        parent.lastChild.remove();
      }
    }
  }

  clearChildren(parentElement) {
    // Assuming removing lastChild is faster than firstChild...
    while (parentElement.lastChild) {
      parentElement.lastChild.remove();
    }
  }

  //#endregion *** Overridden methods ***

  //#region *** Materials ***

  /**
   * Updates the element's (component.displayObject's) backgroundImage url.
   * @param {any} onJitLoadMaterial Will be called if the component's material image has not been
   * loaded yet. It's preferred to preload the material images, but sometimes new product finishes
   * are assigned (from the server-side) before the client's preload code has been updated.
   */
  static async updateMaterialImage(component, onJitLoadMaterial) {
    if (component.isColor) {
      component.displayObject.style.backgroundImage = null;
      component.displayObject.style.backgroundColor = component.color;
    } else if (component.material) {
      const currentImg = component.material.current;
      if (currentImg) {
        if (currentImg.url) {
          component.displayObject.style.backgroundImage = `url("${component.material.current.url}")`;
        } else {
          console.warn(`Not sure what happened here; the image.url was undefined on material ${component.material.id}`);
        }
      } else {
        /* This probably means the product factory didn't list the material as one in use by the
         * product (see ProductViewModelFactory.getAllKnownProductMaterialIds() in subclass). */
        if (onJitLoadMaterial) {
          /* This async callback will await until the low-res image has been loaded, and later,
           * after the hi-res image is loaded, the "inner" callback will be executed. So, we update
           * the backgroundImage twice, first for the lo-res image, and later for the hi-res image. */
          await onJitLoadMaterial(component.material, () => {
            //console.log('Updating url to use the high-res image');
            // Recursive call, but we make sure the onJitLoadMaterial parameter is null to avoid stack overflow:
            HtmlRenderVisitor.updateMaterialImage(component, null);
          });
          //console.log('Setting background image url to use low-res image');
          // Recursive call, but we make sure the onJitLoadMaterial parameter is null to avoid stack overflow:
          HtmlRenderVisitor.updateMaterialImage(component, null);
        } else {
          console.warn(`Material ${component.material.id} was found, but it's image has not been loaded`);
        }
      }
    }
  }

  updateBackgroundImage(component) {
    // Note, it's the user's (owner's) responsibility to assign the this.onJitLoadMaterial callback.
    /*await*/ HtmlRenderVisitor.updateMaterialImage(component,
      async (material, onHisResImgLoaded) => await this.onJitLoadMaterial(material, onHisResImgLoaded));
  }

  //#endregion *** Materials ***

  //#region *** Positioning & Resizing Elements ***

  setBgImageSize(component, element) {
    // Any classes and styles added in the if clauses below must first be reset:
    element.classList.remove('bg-size-fill');
    element.classList.remove('bg-size-cover');
    element.classList.remove('bg-size-crop');
    element.style.backgroundSize = '';

    if (!component.material) {
      element.classList.add('bg-size-fill');
      return;
    }

    const { widthStrategy, heightStrategy } = component.material;
    if (widthStrategy == 'Fill' && heightStrategy == 'Fill') {
      element.classList.add('bg-size-fill');
    } else if (widthStrategy == 'Cover' && heightStrategy == 'Cover') {
      element.classList.add('bg-size-cover');
    } else {
      /* Width = Crop and Height = Proportional means we'll crop the image in the width dimension
       * based on how much of the image width the current product width represents. Height = Proportional
       * then means we'll crop (or display) the height such that the original width/height image
       * ratio is unchanged (we don't want to scale the image in one direction only). */
      const cropWidthAndScaleHeight = widthStrategy == 'Crop' && heightStrategy == 'Proportional';
      const cropHeightAndScaleWidth = heightStrategy == 'Crop' && widthStrategy == 'Proportional';
      if (cropWidthAndScaleHeight || cropHeightAndScaleWidth) {
        /* The background size must be set in code based on how much of the panel's width is used.
          * E.g. if  the panel material is 1000 mm wide (what the image represents), and the current
          * panel is 800 mm wide, then 800/1000 = 80% of the image should be shown in the x-direction.
          * The image should then be scaled in the y-direction while maintaining width/height ratio.
          * The part of the image not needed in the y-direction will automatically be clipped.
          *
          * The background-size as a percent is the percentage of the background positioning area, not
          * of the image width. 50% means display the entire image in half the width of the positioning
          * area. 200% means displaying half the image in the positioning area.
          *
          * Percent = 100 x image width / needed image width
          * Needed Image Width = Image Width * Image Ratio
          * Image Ratio = Product Width / Product Material Width
          *
          * Example:
          * Image Ratio = 800 / 1000 = 0.8
          * Needed Image Width = 2923 px * 0.8 = 2338
          * Percent = 100 x 2923 / 2338 = 125%
          *
          * Solving the equation above:
          * Percent = 100 x Image Width / (Image Width * Image Ratio)
          * Percent = 100 x Image Width / (Image Width * Product Width / Product Material Width)
          * Percent = 100 / (Product Width / Product Material Width)
          *
          * Redoing the example:
          * Percent = 100 / (800 / 1000) = 100 / 0.8 = 125%
          */
        const widthPct = 100 / component.getMaterialWidthRatio(/*use2D:*/ true, this.getRotation());
        const heightPct = 100 / component.getMaterialHeightRatio(/*use2D:*/ true, this.getRotation());
        let width, height;
        const Pct100 = '101%'; // Use 101 insted of 100 to work around sub-pixel rounding issues
        if (cropWidthAndScaleHeight) {
          /* Don't allow a percentage less than 100. It means the product material is not as wide as the
           * panel in the drawing, but we don't want to show any gaps, and will allow the image to scale
           * to cover the entire drawing. */
          width = widthPct <= 100 ? Pct100 : widthPct + '%';
          /* Don't allow percentage less than 100. It means the product material is not as tall as the panel
           * in the drawing, but we don't want to show any gaps, and will allow the image to scale to
           * cover the entire drawing. If heightPct is >= 100, then we'll use auto scaling. */
          //height = heightPct < 100 ? '100%' : 'auto';
          const materialRatio = component.getMaterialWidthHeightRatio();
          const productRatio = component.getWidthHeightRatio(/*use2D:*/ true, this.getRotation());
          height = materialRatio > productRatio ? Pct100 : 'auto';
        } else {
          // Inverse of the above:
          height = heightPct <= 100 ? Pct100 : heightPct + '%';
          width = widthPct <= 100 ? Pct100 : 'auto';
        }
        element.style.backgroundSize = `${width} ${height}`;
        element.classList.add('bg-size-crop');
      }
    }
  }

  ceiling(value, decimals) {
    const factor = Math.pow(10, decimals);
    return (Math.floor(value * factor) / factor) + (1 / factor);
  }

  adjustSizePercent(sizePercent, options) {
    if (options.strategy === SizeStrategy.round) {
      return ObjectUtils.roundDec(sizePercent, options.decimals)
    } else if (options.strategy === SizeStrategy.ceil) {
      return this.ceiling(sizePercent, options.decimals);
    }
    return sizePercent;
  }

  setElementSize(component, element, widthSize, heightSize, options = DefaultSizeOptions, parentWidthCorrection) {
    switch (widthSize) {
      case 'UseProductRatio': {
        let widthPct = component.getWidthRatio(/*width:*/ null, /*use2D:*/ true, this.getRotation(), parentWidthCorrection) * 100;
        /* If the width is NaN we assume it's because the component doesn't have a parent,
         * i.e. it's the root component, In this case we should not set a width style here
         * and rely on the caller to set the style. E.g. see the invalidateDrawingSize()
         * method in the app; it calls the sizer.maximizeWithinParent(...) method to set
         * the width and height styles. */
        if (!isNaN(widthPct)) {
          widthPct = this.adjustSizePercent(widthPct, options.width);
          element.style.width = widthPct + '%';
        }
        break;
      }
    }
    switch (heightSize) {
      case 'UseProductRatio': {
        let heightPct = component.getHeightRatio(/*height:*/ null, /*use2D:*/ true, this.getRotation()) * 100;
        // See comment above for info the if statement below.
        if (!isNaN(heightPct)) {
          heightPct = this.adjustSizePercent(heightPct, options.height);
          element.style.height = heightPct + '%';
        }
        break;
      }
    }
  }

  calcYPositionPct(panel, panelRect) {
    const { top, height } = panelRect;
    const deltaHeight = height - panel.material.productHeight;
    let pct = 0;
    if (deltaHeight !== 0) {
      pct = -100 * top / deltaHeight;
      if (pct > 100) {
        /* If the sum of the height of each panel is larger than than the material product height,
         * we end up with a percentage larger than 100%. That means there will be a gap at the
         * bottom of the panel div. In reality this situation would force the door assembler (person)
         * to use material from 2 boards when putting the door together. We only have one material
         * image so we can't do that. Instead we'll keep the percentage at 100% meaning the top of
         * the bottom panel is the same part of the material image as the bottom of the panel above. */
        pct = 100;
      }
    }
    return pct;
  }

  /**
   * Returns a background image position (background-position-y) such that the the background image
   * is moved vertically for the purpose of displaying different parts of the material image for
   * each panel. This is a private helper method for visitDoorPanel.
   * @param {any} element The html element displaying the panel
   * @param {any} panel The panel object
   */
  getPanelBgImageYPosition(element, panel) {
    const material = panel.material;
    if (material && (material.heightStrategy != 'Fill' && material.heightStrategy != 'Cover')) {
      const core = panel.parent;
      let panelRects = this.currentContext ? this.currentContext.panelRects : null;
      if (!panelRects) {
        panelRects = core.getTopLeftBasedVisualPanelRects();
      }
      const index = core.panels.indexOf(panel);
      const panelRect = panelRects[index];
      const y = this.calcYPositionPct(panel, panelRect);
      return y + '%';
    }
    return '';
  }

  /**
   * We need to set the top margin of the doors element such that each door will be indented into
   * the top track.
   */
  setDoorListMarginTop(front, doorListElement) {
    const doorRect = front.doors[0].rect;
    const top = front.rect.height - (doorRect.bottom + doorRect.height);
    const topRatio = top / front.rect.height;
    // We need to subtract the top track's height ratio from the door list's top ratio
    const trackHeightRatio = front.topTrack.getHeightRatio();
    // Set top margin on the doors element equal to the difference between the ratios:
    let ratioDiffPct = (topRatio - trackHeightRatio) * 100;
    // TODO: Trying to round to nearest 1 decimal to see if that fixes gaps in the display.
    ratioDiffPct = ObjectUtils.roundDec(ratioDiffPct, 1);
    //doorListElement.style.marginTop = ratioDiffPct + '% !important'; // Doesn't work
    doorListElement.style.setProperty('margin-top', ratioDiffPct + '%', 'important');
  }

  /**
   * We need to set the bottom margin of the doors element such that each door's bottom position in
   * percent of the front's height equals the door object's bottom position. Note, the server's
   * Store api has moved the doorlist component's bottom position to each door.
   */
  setDoorListMarginBottom(front, doorListElement) {
    const bottom = front.doors[0].rect.bottom;
    const bottomRatio = bottom / front.rect.height;
    // We need to subtract the bottom track's height ratio from the door list's bottom ratio
    const trackHeightRatio = front.bottomTrack.getHeightRatio();
    // Set bottom margin on the doors element equal to the difference between the ratios:
    let ratioDiffPct = (bottomRatio - trackHeightRatio) * 100;
    // TODO: Trying to round to nearest 1 decimal to see if that fixes gaps in the display.
    ratioDiffPct = ObjectUtils.roundDec(ratioDiffPct, 1);
    //doorListElement.style.marginBottom = ratioDiffPct + '% !important'; // Doesn't work
    doorListElement.style.setProperty('margin-bottom', ratioDiffPct + '%', 'important');
  }

  setRelativeChildPosition(component, element, setLeft, setTop, leftCorrection, parentWidthCorrection) {
    if (setLeft) {
      const left = component.getRelative2dLeft(leftCorrection, parentWidthCorrection);
      if (left === 0) {
        element.style.removeProperty('left');
      } else {
        element.style.left = (left * 100) + '%';
      }
    }
    if (setTop) {
      const top = component.getRelative2dTop();
      if (top === 0) {
        element.style.removeProperty('top');
      } else {
        element.style.top = (top * 100) + '%';
      }
    }
  }

  //#endregion *** Positioning & Resizing Elements ***

  //#region *** Element Helper Methods ***

  createDivElement(classes) {
    const elem = document.createElement('div');
    elem.className = classes;
    return elem;
  }

  createComponentElement(classes, component) {
    const element = this.createDivElement(classes);
    component.displayObject = element;
    this.updateBackgroundImage(component);
    return element;
  }

  /** Adds the childElement to the parent of component */
  addChildElementToParentOf(component, childElement) {
    if (component.parent && component.parent.displayObject) {
      component.parent.displayObject.appendChild(childElement);
    } else {
      this.rootElement.appendChild(childElement);
    }
    return childElement;
  }

  /** Adds the childElement as a child of the current element (stored in currentContext.data). */
  addChildElement(childElement) {
    /* It's a bit strange, but we'll allow calling this method without having a parent element to
     * add the child element to. The original use case is to create isolated html elements which
     * will later be added to the dom (using the HtmlDrawingService methods). */
    if (this.currentContext) {
      this.currentContext.data.appendChild(childElement);
    }
    return childElement;
  }

  /**
   * Creates a new DIV element with classname 'prod-comp'. If component is assigned, then the
   * component's displayObject will be set to the new element. If we have a current context,
   * then we'll also add the element to the parent element (currentContext.data).
   */
  addEmptyDiv(component, className) {
    const newElement = this.createDivElement('prod-comp ' + className);
    if (component) {
      component.displayObject = newElement;
    }
    return this.addChildElement(newElement);
  }

  /**
   * Looks at this.isInlineUpdate to determine whether to create a new html element or reuse the
   * component's displayObject.
   * @param {string} classNames Class names to be set on the element if created.
   * @param {bool} pushContext true/false; if true, the element will be pushed onto the context stack.
   */
  getDivElementAndPushContext(component, classNames, pushContext, onLookupElement) {
    let element;
    if (this.isInlineUpdate) {
      element = component ? component.displayObject : onLookupElement(component, classNames);
    } else {
      element = this.addEmptyDiv(component, classNames);
    }
    if (pushContext) {
      this.pushContext(element);
    }
    return element;
  }

  //#endregion *** Element Helper Methods ***

  //#region *** Element Creation/Rendering Methods ***

  /** Will set height to 100% and width to specific px based on width/height ratio. */
  renderDivWithFullHeight(component, className) {
    const newElement = this.createDivElement('prod-comp ' + className);
    component.displayObject = newElement;

    const clientHeight = component.parent && component.parent.displayObjectHeight
      ? component.parent.displayObjectHeight
      : this.drawingElement.clientHeight;
    // const clientWidth = component.parent && component.parent.displayObjectWidth
    //   ? component.parent.displayObjectWidth
    //   : this.drawingElement.clientWidth;

    const widthRatio = component.getWidthHeightRatio(/*use2D:*/ true, this.getRotation());
    const width = clientHeight * widthRatio;

    //component.displayObjectWidth = width;
    component.displayObjectHeight = clientHeight;

    newElement.style.width = width + 'px';
    newElement.style.height = '100%';

    return this.addChildElementToParentOf(component, newElement);
  }

  renderStandardElement(component, classname, widthSize, heightSize, callback, sizeOptions) {
    let element;
    if (this.isInlineUpdate && component.displayObject) {
      element = component.displayObject;
      // Updates the element's backgroundImage url:
      this.updateBackgroundImage(component);
    } else {
      // Creates the element and then calls updateFinish():
      element = this.createComponentElement('prod-comp ' + classname, component);
    }
    this.setElementSize(component, element, widthSize, heightSize, sizeOptions);
    this.setBgImageSize(component, element);
    if (callback) {
      callback(element);
    }
    if (!element.parentNode) {
      this.addChildElement(element);
    }
    return element;
  }

  renderFlexiSectionContent(content, classes) {
    const element = this.renderStandardElement(content, classes + ' section-content', 'None', 'UseProductRatio', null, OrganizerSizeOptions);
    const top = content.getRelative2dTop();
    if (top === 0) {
      element.style.removeProperty('top');
    } else {
      element.style.top = (top * 100) + '%';
    }
    return element;
  }

  renderFloorModule(module, classNames, onVisitContents) {
    const moduleElement = this.getDivElementAndPushContext(module, 'floor-module ' + classNames, true);

    const { leftSide, rightSide } = module;
    if (leftSide) {
      this.visit(leftSide);
    }

    onVisitContents(module, moduleElement);

    if (rightSide) {
      this.visit(rightSide);
    }

    this.popContext();
    return moduleElement;
  }

  renderStandardModule(module, classNames, beforeVisitContents) {
    // The renderFloorModule method will render any flexi-sides. Anything in between should be
    // rendered in the callback below.
    const moduleElement = this.renderFloorModule(module, 'standard-module ' + classNames,
      (mdule, mduleElement) => {
        /* We are now in between the flexi-sides (if any). Let's create a parent flex element for each
         * floor module and other content: */
        const contentsElement = this.getDivElementAndPushContext(null, 'center-col', true, (comp, classes) => {
          /* (when this.isInlineUpdate == true) because this html element is not associated with any
           * component, we can't lookup the element from component.displayObject. So we must do it
           * manually here: */
          return mduleElement.querySelector('.standard-module > .center-col');
        });
        if (beforeVisitContents) {
          beforeVisitContents(mdule, contentsElement);
        }

        let leftCorrection;
        let parentWidthCorrection;
        if (mdule.leftSide) {
          /* Each floor module's left position is relative to the standard module, but in the html
           * the left position is relative to the contents element which is offset to the right by
           * the left flexi-side's thickness. So, we subtract the thickness from each floor module
           * in the foreach loop below. */
          leftCorrection = -mdule.leftSide.rect.depth;
          /* ...and the parent's width needs to be corrected for any flexi side thicknesses as well: */
          parentWidthCorrection = leftCorrection;
        }
        if (mdule.rightSide) {
          parentWidthCorrection -= mdule.rightSide.rect.depth;
        }
        mdule.floorModules.forEach(floorModule => {
          const element = this.visit(floorModule);
          this.setRelativeChildPosition(floorModule, element, true, true, leftCorrection, parentWidthCorrection);
          this.setElementSize(floorModule, element, 'UseProductRatio', 'UseProductRatio',
            OrganizerSizeOptions, parentWidthCorrection);
        });
        mdule.otherComponents.forEach(component => {
          const element = this.visit(component);
          this.setRelativeChildPosition(component, element, true, true, leftCorrection, parentWidthCorrection);
          this.setElementSize(component, element, 'UseProductRatio', 'UseProductRatio',
            OrganizerSizeOptions, parentWidthCorrection);
        });

        this.popContext(); // moduleElement
      });
    return moduleElement;
  }

  //#endregion *** Element Creation/Rendering Methods ***

  //#region *** Main Visitation Method ***

  visitDoorPanel(panel) {
    return this.renderStandardElement(panel, 'panel', 'None', 'UseProductRatio',
      element => {
        element.style.backgroundPositionY = this.getPanelBgImageYPosition(element, panel);
        if (panel.parent && panel.parent.design === 'Topaz') {
          // Add a css shadow to Topaz panels:
          element.classList.add('shadow');
        }
      });
  }

  visitDoorDivider(divider) {
    return this.renderStandardElement(divider, 'divider', 'None', 'UseProductRatio');
  }

  visitDoorRail(rail) {
    return this.renderStandardElement(rail, 'rail', 'None', 'UseProductRatio');
  }

  visitDoorStile(stile) {
    return this.renderStandardElement(stile, 'stile', 'UseProductRatio', 'None',
      element => {
        if (stile.isLeft) {
          /* The stile images are of the right stile. To display on the left side,
           * flip the image or rotate 180 degrees: */
          element.classList.add('rotate-180');
        }
        if (stile.design === 'Topaz') {
          // Add a css shadow to Topaz stiles:
          element.classList.add('shadow');
        }
      });
  }

  visitDoorCore(core) {
    let div;
    if (this.isReplacingDoorCore) {
      /* When isReplacingDoorCore is true, the core element already exists and we just want to add
       * child panels and dividers to it. */
      div = core.displayObject;
    } else {
      div = this.getDivElementAndPushContext(core, 'core', false);
      /* 11/1/2020: Safari (on iOS at least) requires the parent element to have a height in order
       * for its children to use percentage heights. Chrome doesn't require this as long as there's
       * a Flex property specifying height (i.e. flex-grow). So, for Chrome the height in percent
       * is not needed, while on Safari iOS it is (as of 11/1/2020). */
      let heightPct = core.getHeightRatio() * 100;
      div.style.height = heightPct + '%';
    }
    const panelRects = core.getTopLeftBasedVisualPanelRects();
    this.visitChildren(core, div, { panelRects });
    return div;
  }

  visitDoor(door) {
    const doorElement = this.getDivElementAndPushContext(door, 'door', true);

    this.setElementSize(door, doorElement, 'UseProductRatio', 'None', DoorSizeOptions);
    if (door.parent) {
      const leftOverlap = door.parent.getLeftOverlap(door);
      // Use a negative left margin to shift the door to the left (overlap):
      let overlapPct = -door.getWidthRatio(leftOverlap) * 100;
      if (!isNaN(overlapPct) && overlapPct !== 0) {
        // TODO: Trying to round to nearest 1 decimal to see if that fixes gaps in the display.
        overlapPct = ObjectUtils.roundDec(overlapPct, 1);
        doorElement.style.setProperty('margin-left', overlapPct + '%', 'important');
      }
    }
    // Use z-index to place doors on rails
    doorElement.style.zIndex = door.railNo ? door.railNo : 0;

    const { frame, core } = door;
    this.visit(frame.leftStile);

    this.getDivElementAndPushContext(null, 'center-col', true, (comp, classes) => {
      /* (when this.isInlineUpdate == true) because this html element is not associated with any
       * component, we can't lookup the element from component.displayObject. So we must do it
       * manually here: */
      return doorElement.querySelector('.center-col');
    });

    this.visit(frame.topRail);
    this.visit(core);
    this.visit(frame.bottomRail);
    this.popContext();

    this.visit(frame.rightStile);
    return this.popContext().data;
  }

  visitDoorBottomTrack(track) {
    return this.renderStandardElement(track, 'doortrack', 'None', 'UseProductRatio');
  }

  visitDoorTopTrack(track) {
    return this.renderStandardElement(track, 'doortrack', 'None', 'UseProductRatio');
  }

  visitDoorFront(front) {
    /*
     * 9/14/2020: When the front is the root product in the canvas, it's size and position are driven by:
     * - It's a flex item and the parent's orientation is "row" (left-to-right)
     * - The parent uses justify-content: center which means the front will be horizontally centered
     * - It's flex-grow is not set so by default = 0 which means we must set a width (both width and
     *   height are set in javascript in our method HtmlDrawingEngine.bestFitStageAndCanvas() to
     *   maximize the product drawing size while maintaining its width/height ratio).
     * - The parent's align-items is not set so by default = 'stretch' which means if we don't set
     *   a height, the full height will be used. But we are specifying the height in javascript (see
     *   above).
     */
    const frontElement = this.getDivElementAndPushContext(front, 'doorfront', true);
    this.visit(front.topTrack);

    const doorListElement = this.getDivElementAndPushContext(null, 'doors', true, (comp, classes) => {
      /* (when this.isInlineUpdate == true) because this html element is not associated with any
       * component, we can't lookup the element from component.displayObject. So we must do it
       * manually here: */
      return frontElement.querySelector('.doors');
    });
    this.setDoorListMarginBottom(front, doorListElement);
    this.setDoorListMarginTop(front, doorListElement);

    front.doors.forEach(door => door.acceptVisitor(this));
    this.popContext();

    this.visit(front.bottomTrack);
    return this.popContext().data;
  }

  visitSideWall(sideWall) {
    return this.renderStandardElement(sideWall, 'sidewall', 'None', 'None');
  }

  visitFlexiSide(flexiSide) {
    return this.renderStandardElement(flexiSide, 'flexiside', 'UseProductRatio', 'None', null, OrganizerSizeOptions);
  }

  visitHangRodWithBrackets(hangRod) {
    return this.renderStandardElement(hangRod, 'hangrod', 'UseProductRatio', 'UseProductRatio', null, OrganizerSizeOptions);
  }

  visitSupportRodWithBrackets(supportRod) {
    return this.renderStandardElement(supportRod, 'supportrod', 'UseProductRatio', 'UseProductRatio', null, OrganizerSizeOptions);
  }

  visitFlexiBasketAndTracks(basket) {
    return this.renderStandardElement(basket, 'basket', 'None', 'UseProductRatio', null, OrganizerSizeOptions);
  }

  visitMeshBasketAndTracks(basket) {
    return this.renderStandardElement(basket, 'basket', 'None', 'UseProductRatio', null, OrganizerSizeOptions);
  }

  visitTopShelf(topShelf) {
    return this.renderStandardElement(topShelf, 'topshelf', 'UseProductRatio', 'UseProductRatio', null, OrganizerSizeOptions);
  }

  visitFlexiShelf(flexiShelf) {
    return this.renderStandardElement(flexiShelf, 'flexishelf', 'None', 'UseProductRatio', null, OrganizerSizeOptions);
  }

  visitFlexiSectionContents(contents) {
    const contentsElement = this.getDivElementAndPushContext(contents, 'center-col section-contents');

    this.pushContext(contentsElement);
    contents.components.forEach(content => {
      const element = this.visit(content);
      element.classList.add('section-content');
      this.setRelativeChildPosition(content, element, false, true);
    });
    this.popContext();

    return contentsElement;
  }

  visitFlexiSection(section) {
    const sectionElement = this.renderFloorModule(section, 'flexi-section', sction => {
      this.visit(sction.contents);
    });
    return sectionElement;
  }

  visitHangRodModule(module) {
    // The hangrod module is a standard module, typically within an organizer:
    const moduleElement = this.renderStandardModule(module, 'hangrod-module', (mdule, contentsElement) => {
      mdule.hangRods.forEach(rod => {
        const element = this.visit(rod);
        this.setRelativeChildPosition(rod, element, true, true);
      });
      mdule.supportRods.forEach(rod => {
        const element = this.visit(rod);
        this.setRelativeChildPosition(rod, element, true, true);
      });
      mdule.shelves.forEach(shelf => {
        const element = this.visit(shelf);
        this.setRelativeChildPosition(shelf, element, true, true);
      });
    });
    return moduleElement;
  }

  visitTopShelfModule(module) {
    const moduleElement = this.getDivElementAndPushContext(module, 'topshelf-module', true);
    this.setElementSize(module, moduleElement, 'None', 'UseProductRatio', OrganizerSizeOptions);
    module.components.forEach(shelf => {
      const element = this.visit(shelf);
      this.setRelativeChildPosition(shelf, element, true, true);
    });
    return this.popContext().data;
  }

  visitClosetOrganizer(organizer) {
    // The organizer is a standard module, possibly within another organizer:
    const organizerElement = this.renderStandardModule(organizer, 'closet-organizer', org => {
      this.visit(org.topShelfModule);
    });
    return organizerElement;
  }

  //#endregion *** Main Visitation Method ***
}

/**
 * Updates the backgroundImage css style on each div (component) which has a material. When adding
 * new product/component types to the framework, then a method for each which has a material must
 * be added to this class.
 */
export class HtmlUpdateMaterialImageVisitor extends UpdateMaterialImageVisitor {
  updateMaterialImage(component) {
    HtmlRenderVisitor.updateMaterialImage(component);
  }
}

export class HtmlLabelVisitor extends ToggleLabelVisitor {
  constructor(toggleOn, getProductFinishName) {
    super(toggleOn);
    this.getProductFinishName = getProductFinishName;
  }

  static isLabelVisible(component) {
    return component.finishLabel && component.finishLabel.parentNode === component.displayObject;
  }

  toggleLabel(component) {
    let label = component.finishLabel;
    if (this.toggleOn) {
      if (!label) {
        component.finishLabel = label = document.createElement('span');
        label.className = 'product-label';
      }
      if (!HtmlLabelVisitor.isLabelVisible(component)) {
        component.displayObject.appendChild(label);
      }
    } else if (HtmlLabelVisitor.isLabelVisible(component)) {
      component.displayObject.removeChild(label);
    }
    // Always update the label content (even if the label isn't currently displayed):
    if (label) {
      label.textContent = this.getProductFinishName(component.finish);//component.material.finish;
    }
  }
}
